"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, ShieldCheck, Building2, Stethoscope, Truck } from "lucide-react";
import Link from "next/link";
import { portfolioData } from "@/data/portfolioData";

export const Act35CaseStudies: React.FC = () => {
  const icons: Record<string, React.ElementType> = {
    "apex-logistics-crm": Truck,
    "dentin-luxury-portal": Stethoscope,
    "horizon-real-estate-erp": Building2,
  };

  const projectImages: Record<string, string> = {
    "apex-logistics-crm": "/images/storytelling/case-studies/apex-logistics.jpg",
    "dentin-luxury-portal": "/images/projects/pharmaengage-hero.png",
    "horizon-real-estate-erp": "/images/storytelling/case-studies/horizon-properties.jpg",
  };

  return (
    <section id="scene-proof" className="relative w-full py-24 px-6 sm:px-8 bg-white text-[#111318] border-b border-[#111318]/10">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 pb-8 border-b border-[#111318]/10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-[#2563EB] uppercase font-semibold mb-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>CLIENT SYSTEMS PROOF</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight">
              SYSTEMS WE ENGINEERED FOR CLIENTS.
            </h2>
          </div>
          <p className="text-base text-[#5E636B] max-w-md leading-relaxed">
            Verified production outcomes. Real businesses that replaced fractured workarounds with high-concurrency custom systems engineered by Johnson Softwares.
          </p>
        </div>

        {/* Clean Editorial Case Study Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {portfolioData.map((item, idx) => {
            const IconComponent = icons[item.slug] || Building2;
            const bannerSrc = projectImages[item.slug] || "/images/storytelling/office/web-development.webp";

            return (
              <motion.div
                key={item.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.12, duration: 0.6 }}
                className="rounded-3xl bg-[#F8F8F6] border border-[#111318]/10 hover:border-[#2563EB]/40 transition-all p-6 sm:p-8 flex flex-col justify-between shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_16px_40px_rgb(0,0,0,0.06)] group overflow-hidden"
              >
                <div>
                  {/* Dominant Real Project Visual */}
                  <div className="relative h-48 w-full rounded-2xl overflow-hidden mb-6 bg-slate-200 border border-[#111318]/5">
                    <Image
                      src={bannerSrc}
                      alt={item.client}
                      fill
                      sizes="(max-width: 1024px) 100vw, 33vw"
                      className="object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Category Bar */}
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#111318]/10 text-[#2563EB] flex items-center justify-center shadow-xs">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-mono tracking-wider px-3 py-1 rounded-full bg-white border border-[#111318]/10 text-[#5E636B] font-semibold">
                      {item.scale.toUpperCase()} // {item.industry.toUpperCase()}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-[#111318] group-hover:text-[#2563EB] transition-colors mb-3">
                    {item.client}
                  </h3>
                  <p className="text-xs text-[#5E636B] mb-6 leading-relaxed">
                    {item.summary}
                  </p>

                  {/* Primary Metrics Highlight */}
                  <div className="p-5 rounded-2xl bg-white border border-[#111318]/10 mb-6 space-y-2.5 shadow-sm">
                    <div className="text-[10px] font-mono tracking-wider text-[#5E636B] uppercase font-semibold">
                      VERIFIED PRODUCTION METRICS
                    </div>
                    {item.results.metrics.map((metric, mIdx) => (
                      <div key={mIdx} className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                        <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                        <span>{metric}</span>
                      </div>
                    ))}
                  </div>

                  {/* Tech Stack Chips */}
                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {item.tech.slice(0, 4).map((tech) => (
                      <span
                        key={tech}
                        className="text-[10px] font-mono px-2.5 py-1 rounded-md bg-white border border-[#111318]/10 text-[#5E636B]"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Bottom Link to Full Case Study */}
                <div className="pt-5 border-t border-[#111318]/10 flex items-center justify-between">
                  <span className="text-xs text-[#5E636B] font-mono">
                    {item.feedback.author.split(" ")[0]} ({item.feedback.role.split(",")[0]})
                  </span>
                  <Link
                    href={`/case-studies/${item.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2563EB] hover:text-blue-700 transition-colors group-hover:translate-x-0.5"
                  >
                    <span>Read Architecture</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
